import type {
  ActionSpec,
  ComponentDef,
  ConsiderationDef,
  FieldDef,
  ItemTypeDef,
  MetricDef,
  PrimitivesInfo,
  SimulationDef,
} from "../types";
import {
  AddButton,
  CheckboxInput,
  JsonInput,
  NumberInput,
  PrimitiveRefEditor,
  RefListEditor,
  Row,
  Section,
  SelectInput,
  TextInput,
} from "./controls";

export interface FormEditorProps {
  definition: SimulationDef;
  primitives: PrimitivesInfo | null;
  onChange: (definition: SimulationDef) => void;
}

const DTYPES = ["float64", "float32", "int64", "int32", "bool"];
const COMPONENT_KINDS = ["array", "static", "object"];
const OBJECT_KINDS = ["beliefs", "inventory", "execution", "relationships", "order"];

export function FormEditor({ definition: d, primitives, onChange }: FormEditorProps) {
  if (!primitives) return <div className="pane-empty">loading vocabulary…</div>;
  const p = primitives;
  const set = <K extends keyof SimulationDef>(key: K, value: SimulationDef[K]) =>
    onChange({ ...d, [key]: value });
  const setAt = <T,>(items: T[], index: number, next: T): T[] =>
    items.map((item, i) => (i === index ? next : item));
  const dropAt = <T,>(items: T[], index: number): T[] => items.filter((_, i) => i !== index);

  return (
    <div className="form-editor">
      <Section title="space">
        <SelectInput
          label="kind"
          value={d.space.kind}
          options={["graph"]}
          onChange={(kind) => set("space", { ...d.space, kind })}
        />
        <JsonInput
          label='topology — {"generator": "grid", "width": 8, "height": 8} | ring/path {"n": N} | {"edges": [...]}'
          value={d.space.topology}
          onChange={(topology) => set("space", { ...d.space, topology })}
        />
        <TextInput
          label="position component"
          value={d.space.position_component}
          onChange={(v) => set("space", { ...d.space, position_component: v })}
        />
        <NumberInput
          label="perception radius"
          value={d.space.perception_radius}
          min={0}
          onChange={(v) => set("space", { ...d.space, perception_radius: v })}
        />
      </Section>

      <Section title="components" count={d.components.length}>
        {d.components.map((c, i) => (
          <Row key={i} onRemove={() => set("components", dropAt(d.components, i))}>
            <ComponentRow
              component={c}
              onChange={(next) => set("components", setAt(d.components, i, next))}
            />
          </Row>
        ))}
        <AddButton
          label="component"
          onClick={() =>
            set("components", [
              ...d.components,
              {
                name: "",
                kind: "array",
                dtype: "float64",
                fill: 0,
                double_buffered: true,
                object_kind: null,
                capacity: null,
              },
            ])
          }
        />
      </Section>

      <Section title="fields" count={d.fields.length}>
        {d.fields.map((f, i) => (
          <Row key={i} onRemove={() => set("fields", dropAt(d.fields, i))}>
            <FieldRow field={f} onChange={(next) => set("fields", setAt(d.fields, i, next))} />
          </Row>
        ))}
        <AddButton
          label="field"
          onClick={() =>
            set("fields", [...d.fields, { name: "", dtype: "float64", fill: 0, initial: {} }])
          }
        />
      </Section>

      <Section title="item types" count={d.item_types.length}>
        {d.item_types.map((t, i) => (
          <Row key={i} onRemove={() => set("item_types", dropAt(d.item_types, i))}>
            <ItemTypeRow
              itemType={t}
              verbs={p.verbs}
              onChange={(next) => set("item_types", setAt(d.item_types, i, next))}
            />
          </Row>
        ))}
        <AddButton
          label="item type"
          onClick={() =>
            set("item_types", [
              ...d.item_types,
              { name: "", affords: [], consumable: false, value: 0.1, max_condition: 1, properties: {} },
            ])
          }
        />
      </Section>

      <Section title="agents & placement" count={d.agents.placement.length}>
        <NumberInput
          label="count"
          value={d.agents.count}
          min={1}
          onChange={(count) => set("agents", { ...d.agents, count })}
        />
        <RefListEditor
          title="placement"
          names={p.placements}
          items={d.agents.placement}
          onChange={(placement) => set("agents", { ...d.agents, placement })}
        />
      </Section>

      <Section title="actions" count={d.actions.length}>
        {d.actions.map((a, i) => (
          <ActionCard
            key={i}
            action={a}
            p={p}
            onChange={(next) => set("actions", setAt(d.actions, i, next))}
            onRemove={() => set("actions", dropAt(d.actions, i))}
          />
        ))}
        <AddButton
          label="action"
          onClick={() =>
            set("actions", [
              ...d.actions,
              {
                verb: p.verbs[0] ?? "IDLE",
                eligible: { name: "always", params: {} },
                effect: [],
                considerations: [],
                base_weight: 1,
                build_params: null,
                duration: 1,
                interruptible: true,
                tick_effect: [],
              },
            ])
          }
        />
      </Section>

      <Section title="world systems" count={d.world_systems.length}>
        <RefListEditor
          title="world system"
          names={p.primitives.world_system ?? []}
          items={d.world_systems}
          onChange={(v) => set("world_systems", v)}
        />
      </Section>

      <Section title="perception" count={d.perception.length}>
        <RefListEditor
          title="perception"
          names={p.primitives.perception ?? []}
          items={d.perception}
          onChange={(v) => set("perception", v)}
        />
      </Section>

      <Section title="metrics" count={d.metrics.length}>
        {d.metrics.map((m, i) => (
          <Row key={i} onRemove={() => set("metrics", dropAt(d.metrics, i))}>
            <MetricRow
              metric={m}
              names={p.primitives.metric ?? []}
              onChange={(next) => set("metrics", setAt(d.metrics, i, next))}
            />
          </Row>
        ))}
        <AddButton
          label="metric"
          onClick={() =>
            set("metrics", [
              ...d.metrics,
              { name: "", metric: { name: p.primitives.metric?.[0] ?? "", params: {} }, every: 1 },
            ])
          }
        />
      </Section>

      <Section title="policy">
        <SelectInput
          label="kind"
          value={d.policy.kind}
          options={p.policies}
          onChange={(kind) => set("policy", { ...d.policy, kind })}
        />
        <JsonInput
          label="params"
          value={d.policy.params}
          onChange={(params) => set("policy", { ...d.policy, params })}
        />
      </Section>

      <Section title="arbitrator">
        <SelectInput
          label="kind"
          value={d.arbitrator.kind}
          options={p.arbitrators}
          onChange={(kind) => set("arbitrator", { ...d.arbitrator, kind })}
        />
        <JsonInput
          label="params"
          value={d.arbitrator.params}
          onChange={(params) => set("arbitrator", { ...d.arbitrator, params })}
        />
      </Section>

      <Section title="execution">
        <CheckboxInput
          label="timed execution"
          checked={d.execution !== null}
          onChange={(on) =>
            set("execution", on ? { component: "execution", commitment_bonus: 0.1 } : null)
          }
        />
        {d.execution && (
          <>
            <TextInput
              label="component"
              value={d.execution.component}
              onChange={(component) => set("execution", { ...d.execution!, component })}
            />
            <NumberInput
              label="commitment bonus"
              value={d.execution.commitment_bonus}
              step={0.05}
              onChange={(commitment_bonus) =>
                set("execution", { ...d.execution!, commitment_bonus })
              }
            />
          </>
        )}
      </Section>

      <Section title="lifecycle">
        <CheckboxInput
          label="mortality (active mask)"
          checked={d.lifecycle !== null}
          onChange={(on) => set("lifecycle", on ? { component: "alive" } : null)}
        />
        {d.lifecycle && (
          <TextInput
            label="component"
            value={d.lifecycle.component}
            onChange={(component) => set("lifecycle", { component })}
          />
        )}
      </Section>
    </div>
  );
}

function ComponentRow(props: { component: ComponentDef; onChange: (c: ComponentDef) => void }) {
  const { component: c, onChange } = props;
  return (
    <>
      <TextInput label="name" value={c.name} onChange={(name) => onChange({ ...c, name })} />
      <SelectInput
        label="kind"
        value={c.kind}
        options={COMPONENT_KINDS}
        onChange={(kind) => onChange({ ...c, kind, double_buffered: kind === "array" })}
      />
      <SelectInput
        label="dtype"
        value={c.dtype}
        options={DTYPES}
        onChange={(dtype) => onChange({ ...c, dtype })}
      />
      <NumberInput label="fill" value={c.fill} step={0.1} onChange={(fill) => onChange({ ...c, fill })} />
      {c.kind === "object" && (
        <SelectInput
          label="object kind"
          value={c.object_kind ?? ""}
          options={OBJECT_KINDS}
          allowEmpty
          onChange={(v) => onChange({ ...c, object_kind: v || null })}
        />
      )}
      {c.object_kind === "inventory" && (
        <NumberInput
          label="capacity"
          value={c.capacity ?? 1}
          min={1}
          onChange={(capacity) => onChange({ ...c, capacity })}
        />
      )}
    </>
  );
}

function FieldRow(props: { field: FieldDef; onChange: (f: FieldDef) => void }) {
  const { field: f, onChange } = props;
  return (
    <>
      <TextInput label="name" value={f.name} onChange={(name) => onChange({ ...f, name })} />
      <SelectInput
        label="dtype"
        value={f.dtype}
        options={DTYPES}
        onChange={(dtype) => onChange({ ...f, dtype })}
      />
      <NumberInput label="fill" value={f.fill} step={0.1} onChange={(fill) => onChange({ ...f, fill })} />
      <JsonInput
        label='initial — {"node": value}'
        value={f.initial}
        onChange={(initial) => onChange({ ...f, initial: initial as Record<string, number> })}
      />
    </>
  );
}

function ItemTypeRow(props: {
  itemType: ItemTypeDef;
  verbs: string[];
  onChange: (t: ItemTypeDef) => void;
}) {
  const { itemType: t, onChange } = props;
  return (
    <>
      <TextInput label="name" value={t.name} onChange={(name) => onChange({ ...t, name })} />
      <TextInput
        label="affords (comma-separated verbs)"
        value={t.affords.join(", ")}
        onChange={(text) =>
          onChange({ ...t, affords: text.split(",").map((s) => s.trim()).filter(Boolean) })
        }
      />
      <CheckboxInput
        label="consumable"
        checked={t.consumable}
        onChange={(consumable) => onChange({ ...t, consumable })}
      />
      <NumberInput label="value" value={t.value} step={0.05} onChange={(value) => onChange({ ...t, value })} />
      <NumberInput
        label="max condition"
        value={t.max_condition}
        step={0.1}
        onChange={(max_condition) => onChange({ ...t, max_condition })}
      />
      <JsonInput
        label="properties"
        value={t.properties}
        onChange={(properties) => onChange({ ...t, properties })}
      />
    </>
  );
}

function MetricRow(props: { metric: MetricDef; names: string[]; onChange: (m: MetricDef) => void }) {
  const { metric: m, names, onChange } = props;
  return (
    <>
      <TextInput label="name" value={m.name} onChange={(name) => onChange({ ...m, name })} />
      <PrimitiveRefEditor
        label="metric"
        names={names}
        value={m.metric}
        onChange={(metric) => onChange({ ...m, metric })}
      />
      <NumberInput label="every" value={m.every} min={1} onChange={(every) => onChange({ ...m, every })} />
    </>
  );
}

function ActionCard(props: {
  action: ActionSpec;
  p: PrimitivesInfo;
  onChange: (a: ActionSpec) => void;
  onRemove: () => void;
}) {
  const { action: a, p, onChange, onRemove } = props;
  const set = <K extends keyof ActionSpec>(key: K, value: ActionSpec[K]) =>
    onChange({ ...a, [key]: value });
  const setCon = (index: number, next: ConsiderationDef) =>
    set("considerations", a.considerations.map((c, i) => (i === index ? next : c)));
  return (
    <div className="action-card">
      <div className="action-head">
        <SelectInput label="verb" value={a.verb} options={p.verbs} onChange={(verb) => set("verb", verb)} />
        <NumberInput
          label="base weight"
          value={a.base_weight}
          step={0.1}
          onChange={(v) => set("base_weight", v)}
        />
        <NumberInput label="duration" value={a.duration} min={1} onChange={(v) => set("duration", v)} />
        <CheckboxInput
          label="interruptible"
          checked={a.interruptible}
          onChange={(v) => set("interruptible", v)}
        />
        <button className="row-remove" title="remove action" onClick={onRemove}>
          ×
        </button>
      </div>
      <PrimitiveRefEditor
        label="eligible"
        names={p.primitives.eligible ?? []}
        value={a.eligible}
        onChange={(v) => set("eligible", v)}
      />
      <div className="action-sub">
        <h4>effects</h4>
        <RefListEditor
          title="effect"
          names={p.primitives.effect ?? []}
          items={a.effect}
          onChange={(v) => set("effect", v)}
        />
      </div>
      <div className="action-sub">
        <h4>tick effects (while executing)</h4>
        <RefListEditor
          title="tick effect"
          names={p.primitives.effect ?? []}
          items={a.tick_effect}
          onChange={(v) => set("tick_effect", v)}
        />
      </div>
      <div className="action-sub">
        <h4>build params</h4>
        {a.build_params ? (
          <>
            <PrimitiveRefEditor
              label="build_params"
              names={p.primitives.build_params ?? []}
              value={a.build_params}
              onChange={(v) => set("build_params", v)}
            />
            <button className="btn btn-ghost" onClick={() => set("build_params", null)}>
              remove build params
            </button>
          </>
        ) : (
          <AddButton
            label="build params"
            onClick={() =>
              set("build_params", { name: p.primitives.build_params?.[0] ?? "", params: {} })
            }
          />
        )}
      </div>
      <div className="action-sub">
        <h4>considerations</h4>
        {a.considerations.map((con, i) => (
          <Row
            key={i}
            onRemove={() =>
              set("considerations", a.considerations.filter((_, j) => j !== i))
            }
          >
            <TextInput label="name" value={con.name} onChange={(name) => setCon(i, { ...con, name })} />
            <PrimitiveRefEditor
              label="input"
              names={p.primitives.input ?? []}
              value={con.input}
              onChange={(input) => setCon(i, { ...con, input })}
            />
            <SelectInput
              label="curve"
              value={con.curve.kind}
              options={p.curves}
              onChange={(kind) => setCon(i, { ...con, curve: { ...con.curve, kind } })}
            />
            <JsonInput
              label="curve params"
              value={con.curve.params}
              onChange={(params) => setCon(i, { ...con, curve: { ...con.curve, params } })}
            />
            <NumberInput
              label="weight"
              value={con.weight}
              step={0.1}
              onChange={(weight) => setCon(i, { ...con, weight })}
            />
          </Row>
        ))}
        <AddButton
          label="consideration"
          onClick={() =>
            set("considerations", [
              ...a.considerations,
              {
                name: "",
                input: { name: p.primitives.input?.[0] ?? "", params: {} },
                curve: { kind: "linear", params: {} },
                weight: 1,
              },
            ])
          }
        />
      </div>
    </div>
  );
}
